// Called inside the replay renderer's world transform: camera, zoom and DPR
// are inherited from the exact transform used to paint the players.
function translatePath(path, delta) {
  return (path || []).map(point => ({ x: Number(point.x) + delta.x, y: Number(point.y) + delta.y }));
}

// Keep the geometry used for painting and pointer hit testing in one place.
// Recommendations are generated around a local origin, so they are anchored
// to the player's actual paused position at render time.
export function trainingPathGeometry(option, selected) {
  const previewFrames = Array.isArray(option?.previewFrames)
    ? option.previewFrames.filter(frame => [frame?.player?.x, frame?.player?.y, frame?.ball?.x, frame?.ball?.y]
      .every(value => Number.isFinite(Number(value))))
    : [];
  // New previews already contain absolute world coordinates. Keep them as the
  // single source for both painting and hit testing; translating them against
  // a later selected-player position would make the animation jump.
  if (previewFrames.length > 0) {
    const movement = previewFrames.map(frame => ({ x: Number(frame.player.x), y: Number(frame.player.y) }));
    const release = previewFrames.map(frame => ({ x: Number(frame.ball.x), y: Number(frame.ball.y) }));
    return {
      absolutePreview: true,
      anchor: { x: 0, y: 0 },
      anchoredMovement: movement,
      anchoredRelease: release,
      releaseDelta: { x: 0, y: 0 },
      previewFrames,
    };
  }
  const movementPath = option?.movementPath || [];
  const origin = movementPath[0];
  const anchor = selected && origin
    ? { x: selected.position.x - origin.x, y: selected.position.y - origin.y }
    : { x: 0, y: 0 };
  const anchoredMovement = translatePath(movementPath, anchor);
  const ballPath = option?.ballPath || [];
  const drawnPath = option?.releasePath?.length > 1 ? option.releasePath : (option?.path || []);
  const releaseStart = ballPath.length > 1 ? ballPath[ballPath.length - 1]
    : (movementPath.length > 1 ? movementPath[movementPath.length - 1] : null);
  const releaseDelta = releaseStart && drawnPath[0]
    ? { x: releaseStart.x + anchor.x - drawnPath[0].x, y: releaseStart.y + anchor.y - drawnPath[0].y }
    : anchor;
  return {
    anchor,
    anchoredMovement,
    anchoredRelease: translatePath(drawnPath, releaseDelta),
    releaseDelta,
  };
}

export function trainingPathHitTargets(paths, selected) {
  return (paths || []).map((option, index) => {
    const geometry = trainingPathGeometry(option, selected);
    const endpoint = geometry.anchoredRelease.at(-1) || geometry.anchoredMovement.at(-1);
    const line = geometry.anchoredRelease.length > 1 ? geometry.anchoredRelease : geometry.anchoredMovement;
    const segments = [];
    for (let i = 1; i < line.length; i += 1) segments.push({ a: line[i - 1], b: line[i] });
    return endpoint ? { option, rank: option.rank || index + 1, point: endpoint, segments, radius: 16 } : null;
  }).filter(Boolean);
}

export function drawTrainingPaths(ctx, paths, selected) {
  ctx.save();
  if (selected) {
    ctx.strokeStyle = '#52edbf'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(selected.position.x, selected.position.y, selected.radius + 5, 0, Math.PI * 2); ctx.stroke();
  }
  [...paths].reverse().forEach(({ path, movementPath, releasePath, ballPath, previewFrames, rank }) => {
    if (!path?.length && !movementPath?.length && !previewFrames?.length) return;
    const colors = ['#53edc0', '#83b5ff', '#e3baff'];
    const geometry = trainingPathGeometry({path, movementPath, releasePath, ballPath, previewFrames}, selected);
    const {anchor, anchoredMovement, anchoredRelease} = geometry;
    // The movement path belongs to the selected player; the release path
    // belongs to the ball. They are drawn separately so a carry recommendation
    // cannot be mistaken for a kick trajectory.
    if (movementPath?.length > 1 || (geometry.absolutePreview && anchoredMovement.length > 1)) {
      ctx.strokeStyle = colors[rank - 1] || colors[0]; ctx.lineWidth = rank === 1 ? 3 : 2;
      ctx.setLineDash(rank === 1 ? [9, 5] : [5, 5]); ctx.beginPath();
      anchoredMovement.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y));
      ctx.stroke(); ctx.setLineDash([]);
      const projectedBall = geometry.absolutePreview ? anchoredRelease.at(-1) : ballPath?.at(-1);
      if (projectedBall) {
        ctx.strokeStyle = '#b9ffe9'; ctx.lineWidth = 1; ctx.setLineDash([3, 4]); ctx.beginPath();
        ctx.moveTo(anchoredMovement.at(-1).x, anchoredMovement.at(-1).y);
        ctx.lineTo(projectedBall.x + (geometry.absolutePreview ? 0 : anchor.x), projectedBall.y + (geometry.absolutePreview ? 0 : anchor.y)); ctx.stroke(); ctx.setLineDash([]);
      }
    }
    const drawnPath = geometry.absolutePreview ? anchoredRelease : (releasePath?.length > 1 ? releasePath : path);
    if (!drawnPath?.length) return;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.setLineDash(rank === 1 ? [] : [7, 5]);
    ctx.lineWidth = rank === 1 ? 3 : 2;
    ctx.strokeStyle = colors[rank - 1] || colors[0];
    // Release paths are projected from a later setup state. Anchor their start
    // to the movement endpoint so a stale interpolation frame cannot create a
    // visible gap beside the selected player.
    ctx.beginPath(); anchoredRelease.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.stroke();
    const end = anchoredRelease[anchoredRelease.length - 1]; const prev = anchoredRelease[anchoredRelease.length - 2] || end;
    const angle = Math.atan2(end.y - prev.y, end.x - prev.x);
    ctx.setLineDash([]); ctx.beginPath();
    ctx.moveTo(end.x - 9 * Math.cos(angle - .45), end.y - 9 * Math.sin(angle - .45));
    ctx.lineTo(end.x, end.y); ctx.lineTo(end.x - 9 * Math.cos(angle + .45), end.y - 9 * Math.sin(angle + .45)); ctx.stroke();
    ctx.fillStyle = '#091728'; ctx.beginPath(); ctx.arc(end.x, end.y - 13, 8, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = colors[rank - 1]; ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(String(rank), end.x, end.y - 9);
  });
  ctx.restore();
}
export function canvasToWorld(canvas, matrix, clientX, clientY) {
  const r = canvas.getBoundingClientRect();
  const x = (clientX - r.left) * canvas.width / r.width;
  const y = (clientY - r.top) * canvas.height / r.height;
  const det = matrix.a * matrix.d - matrix.b * matrix.c;
  return { x: (matrix.d * (x - matrix.e) - matrix.c * (y - matrix.f)) / det,
    y: (-matrix.b * (x - matrix.e) + matrix.a * (y - matrix.f)) / det };
}
